import * as Sentry from '@sentry/react';

const FRONTEND_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const FRONTEND_SENTRY_ENV = process.env.REACT_APP_ENV || 'production';
const FRONTEND_SENTRY_RELEASE = process.env.REACT_APP_RELEASE;

const SENSITIVE_HEADERS = new Set([
	'authorization',
	'cookie',
	'set-cookie',
	'x-api-key',
	'proxy-authorization',
]);

const maskSensitiveHeaders = (headers) => {
	if (!headers || typeof headers !== 'object') return headers;

	const sanitized = { ...headers };
	Object.keys(sanitized).forEach((headerName) => {
		if (!headerName) return;
		if (SENSITIVE_HEADERS.has(headerName.toLowerCase())) {
			sanitized[headerName] = '[Filtered]';
		}
	});
	return sanitized;
};

const removeQueryString = (url) => {
	if (!url || typeof url !== 'string') return url;
	const queryStart = url.indexOf('?');
	return queryStart >= 0 ? url.slice(0, queryStart) : url;
};

const sanitizeUrl = (url) => removeQueryString(url);

export const initSentry = () => {
	if (!FRONTEND_SENTRY_DSN) return;

	Sentry.init({
		dsn: FRONTEND_SENTRY_DSN,
		environment: FRONTEND_SENTRY_ENV,
		release: FRONTEND_SENTRY_RELEASE,
		sendDefaultPii: false,
		tracesSampleRate: 0,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,
		beforeSend(event) {
			if (event?.request?.headers) {
				event.request.headers = maskSensitiveHeaders(event.request.headers);
			}

			if (event?.request?.url) {
				event.request.url = removeQueryString(event.request.url);
			}

			return event;
		},
	});
};

export const syncSentryUserContext = (userInfo) => {
	if (userInfo?.id) {
		Sentry.setUser({
			id: String(userInfo.id),
			role: userInfo.rol_id != null ? String(userInfo.rol_id) : undefined,
		});
		Sentry.setTag('rol_id', userInfo.rol_id != null ? String(userInfo.rol_id) : 'unknown');
		Sentry.setTag('is_authenticated', 'true');
		return;
	}

	Sentry.setUser(null);
	Sentry.setTag('rol_id', 'guest');
	Sentry.setTag('is_authenticated', 'false');
};

export const captureError = (error, context = {}) => {
	if (!FRONTEND_SENTRY_DSN) return;

	Sentry.withScope((scope) => {
		if (context.tags) {
			Object.entries(context.tags).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					scope.setTag(key, String(value));
				}
			});
		}

		if (context.extra) {
			scope.setExtras(context.extra);
		}

		if (context.fingerprint) {
			scope.setFingerprint(context.fingerprint);
		}

		Sentry.captureException(error);
	});
};

export const captureAxiosError = (error) => {
	const status = error?.response?.status;
	const shouldReport = !status || status >= 500;

	if (!shouldReport) return;

	captureError(error, {
		tags: {
			error_source: 'axios',
			http_status: status || 'network',
		},
		extra: {
			method: error?.config?.method?.toUpperCase(),
			url: sanitizeUrl(error?.config?.url),
			baseURL: sanitizeUrl(error?.config?.baseURL),
			timeout: error?.config?.timeout,
			code: error?.code,
		},
	});
};
